/**
 * Page 2 of Create trigger drawer
 */

import {TargetBlank} from '@wandb/weave/common/util/links';
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, type IconName} from '@wandb/weave/components/Icon';
import React, {useMemo} from 'react';
import {type FormatOptionLabelMeta} from 'react-select';
import {toast} from 'react-toastify';

import {
  EventTriggeringConditionType,
  useEntityIntegrationsQuery,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import DocUrls from '../../../../util/doc_urls';
import {InstrumentedLoader} from '../../../utility/InstrumentedLoader';
import {useWebhooksFromEntity} from '../../../Webhooks/hooks';
import {SelectEntity} from '../../Queues/SelectEntity';
import {useEntityHasJobs} from '../hooks';
import {type UserCreatableActionType} from '../TriggersTable/utilTypes';
import {useActionConfigStore} from './ActionStore';
import {ARIAConfiguration} from './ARIAConfiguration';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {useEventStore} from './EventStore';
import {JobConfiguration} from './JobConfiguration';
import {NotificationSection, useSlackIntegrations} from './NotificationSection';
import {SeparatedChunk} from './shared';
import {SwitchToBasicButton} from './SwitchToBasicButton';
import {WebhookConfiguration} from './WebhookConfiguration';

export type TriggeredActionOptionType = {
  label: string;
  value: UserCreatableActionType;
  numActions?: number;
};

type Props = {
  onClose: () => void;
};

export const DrawerPageActionConfiguration: React.FC<Props> = ({onClose}) => {
  const entityName = useActionConfigStore(s => s.entityName);
  const actionType = useActionConfigStore(s => s.actionType);
  const setEntityName = useActionConfigStore(s => s.setEntityName);
  const setActionType = useActionConfigStore(s => s.setActionType);
  const ariaTriggerEnabled = useActionConfigStore(
    s => s.featureFlags.ariaTriggerEnabled
  );
  const eventType = useEventStore(s => s.eventType);
  const isUpdating = useEventStore(s => s.initProps.isUpdating);
  // When editing an existing automation, gate changes to the ACTION on the viewer's permissions for the CURRENT action.
  // If they lack permissions, they may still be able to edit other fields (name, event, etc.),
  // so keep the drawer open, but prevent changes to the action that would otherwise fail on save.
  const existingAutomationActionReadOnly = useEventStore(
    s => s.initProps.existingTrigger?.action?.readOnly ?? true
  );
  const actionEditDisabled = isUpdating && existingAutomationActionReadOnly;
  const isJobAction = actionType === 'QueueJobTriggeredAction';
  const isWebhookAction = actionType === 'GenericWebhookTriggeredAction';
  const isSlackAction = actionType === 'NotificationTriggeredAction';
  const actionRequiresTeam = isJobAction || isWebhookAction || isSlackAction;
  // Each lookup is entity-scoped; the hooks skip their query when given
  // undefined, so only the selected action's resources are fetched.
  const {
    webhooks,
    loading: webhooksLoading,
    error: webhooksError,
  } = useWebhooks(isWebhookAction ? entityName : undefined);
  const {
    slackIntegrations,
    loading: slackIntegrationsLoading,
    error: slackIntegrationsError,
  } = useSlackIntegrations(isSlackAction ? entityName : undefined);

  const {
    data: entityHasJobArtifacts,
    loading: jobsLoading,
    error: jobsError,
  } = useEntityHasJobs(isJobAction ? entityName : undefined);
  const triggeredActionTypeOptions: TriggeredActionOptionType[] =
    useMemo(() => {
      const options: TriggeredActionOptionType[] = [];
      if (
        eventType !== EventTriggeringConditionType.RunMetric &&
        // don't currently support editing launch jobs
        !isUpdating
      ) {
        // with deprecation of launch, no current plans to support
        // it for run metric changes
        options.push({
          label: 'Jobs',
          value: 'QueueJobTriggeredAction',
        });
      }
      options.push(
        {
          label: 'Webhooks',
          value: 'GenericWebhookTriggeredAction',
          numActions: webhooks.length,
        },
        {
          label: 'Slack notification',
          value: 'NotificationTriggeredAction',
          numActions: slackIntegrations.length,
        }
      );
      if (ariaTriggerEnabled) {
        options.push({
          label: 'Trigger ARIA',
          value: 'ARIATriggeredAction',
        });
      }

      return options;
    }, [
      eventType,
      isUpdating,
      webhooks.length,
      slackIntegrations.length,
      ariaTriggerEnabled,
    ]);

  let actionTypeOption: TriggeredActionOptionType | null = null;
  if (actionType === 'QueueJobTriggeredAction') {
    actionTypeOption = {
      label: 'Jobs',
      value: 'QueueJobTriggeredAction',
    };
  } else if (actionType === 'GenericWebhookTriggeredAction') {
    actionTypeOption = {
      label: 'Webhooks',
      value: 'GenericWebhookTriggeredAction',
    };
  } else if (actionType === 'NotificationTriggeredAction') {
    actionTypeOption = {
      label: 'Slack notification',
      value: 'NotificationTriggeredAction',
    };
  } else if (actionType === 'PushNotificationTriggeredAction') {
    actionTypeOption = {
      label: 'Push notification',
      value: 'PushNotificationTriggeredAction',
    };
  } else if (actionType === 'ARIATriggeredAction') {
    actionTypeOption = {
      label: 'Trigger ARIA',
      value: 'ARIATriggeredAction',
    };
  } else if (actionType === undefined) {
    actionTypeOption = null;
  } else {
    assertUnreachable(actionType);
  }

  const noJobs = !entityHasJobArtifacts;
  const noWebhooks = webhooks.length === 0;
  const hasActions = !noJobs || !noWebhooks;
  const showActionDetails = !actionRequiresTeam || entityName != null;
  const showNoJobsError = showActionDetails && isJobAction && noJobs;
  const showNoWebhooksError =
    showActionDetails && isWebhookAction && noWebhooks;

  React.useEffect(() => {
    Analytics.track('Automation Creation', {
      type: 'page_view',
      page: 'action_configuration',
      eventType,
    });
    // Only track on initial render
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isJobAction && jobsError) {
    onClose();
    toast.error('Error fetching jobs information');
    return null;
  }
  if (isWebhookAction && webhooksError) {
    onClose();
    toast.error('Error fetching webhooks information');
    return null;
  }

  if ((isJobAction && jobsLoading) || (isWebhookAction && webhooksLoading)) {
    return <InstrumentedLoader active name="create-trigger-drawer-projects" />;
  }
  const showHelpFooter = hasActions || entityName === '';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
      }}>
      <div style={{flex: 1, padding: '2px'}}>
        <S.PageLabelRow>Step 2 of 3 - Action configuration</S.PageLabelRow>

        <S.LabelRow>
          <S.Label>Action type</S.Label>
        </S.LabelRow>
        <Select<TriggeredActionOptionType | null>
          options={triggeredActionTypeOptions}
          placeholder="Select an action type..."
          value={actionTypeOption}
          onChange={(d: TriggeredActionOptionType | null) => {
            if (d) {
              setActionType(d.value);
              Analytics.track('Automation Creation', {
                type: 'action_selected',
                actionType: d.value,
                eventType,
                entityName,
              });
            }
          }}
          formatOptionLabel={formatActionTypeOptionLabel}
          errorState={showNoJobsError || showNoWebhooksError}
          isDisabled={actionEditDisabled}
        />
        {actionEditDisabled && (
          <S.ErrorText>
            You don't have permission to change this action, but you can still
            edit other parts of the automation.
          </S.ErrorText>
        )}
        {actionRequiresTeam && (
          <>
            <S.LabelRow>
              <S.Label>Team</S.Label>
            </S.LabelRow>
            <SelectEntity
              includeAll={false}
              value={entityName ?? ''}
              onSelectEntity={en => {
                setEntityName(en);
              }}
              includePersonalEntity={true}
            />
          </>
        )}
        {showActionDetails && (
          <>
            {actionType === 'GenericWebhookTriggeredAction' &&
              (noWebhooks ? (
                <S.ErrorText>No webhooks available</S.ErrorText>
              ) : (
                <WebhookConfiguration webhooks={webhooks} />
              ))}
            {actionType === 'QueueJobTriggeredAction' &&
              (noJobs ? (
                <S.ErrorText>No jobs available</S.ErrorText>
              ) : (
                <JobConfiguration onClose={onClose} />
              ))}
            {actionType === 'NotificationTriggeredAction' && (
              <NotificationSection
                integrations={slackIntegrations}
                slackIntegrationsLoading={slackIntegrationsLoading}
                slackIntegrationsError={slackIntegrationsError}
              />
            )}
            {actionType === 'ARIATriggeredAction' && <ARIAConfiguration />}
          </>
        )}
        <SeparatedChunk>
          <SwitchToBasicButton />
        </SeparatedChunk>
      </div>
      {showHelpFooter && (
        <div style={{marginTop: 'auto'}}>
          <S.Help>
            <S.HelpMessage>
              Need help? Check out our documentation:
            </S.HelpMessage>
            <TargetBlank href={DocUrls.automation}>
              <Button size="large" variant="secondary" icon="documentation">
                Documentation
              </Button>
            </TargetBlank>
          </S.Help>
        </div>
      )}
    </div>
  );
};

const useWebhooks = (entityName: string | undefined) => {
  const entityIntegrations = useEntityIntegrationsQuery({
    variables: {
      name: entityName ?? '' /* ?'' valid because of skip below */,
    },
    skip: entityName == null,
  });
  const webhooks = useWebhooksFromEntity(entityIntegrations?.data?.entity);
  return {
    webhooks,
    loading: entityIntegrations.loading,
    error: entityIntegrations.error,
  };
};

const formatActionTypeOptionLabel = (
  actionOption: TriggeredActionOptionType | null,
  _context: FormatOptionLabelMeta<TriggeredActionOptionType | null>
) => {
  if (actionOption == null) {
    return;
  }

  const {value, label} = actionOption;
  let iconName: IconName;
  switch (value) {
    case 'GenericWebhookTriggeredAction':
      iconName = 'webhook';
      break;
    case 'QueueJobTriggeredAction':
      iconName = 'flash-bolt';
      break;
    case 'NotificationTriggeredAction':
    case 'PushNotificationTriggeredAction':
      iconName = 'bell-notifications';
      break;
    case 'ARIATriggeredAction':
      iconName = 'lightboard';
      break;
    default:
      assertUnreachable(value);
  }
  // SelectOption already centers via align-items; marginBottom:auto was
  // pulling icons (especially lightboard) above the label baseline.
  return (
    <S.SelectOption>
      <Icon name={iconName} />
      <S.SelectOptionLabel>
        <S.SelectOptionLabelText>{label}</S.SelectOptionLabelText>
      </S.SelectOptionLabel>
    </S.SelectOption>
  );
};
